import axios, { type AxiosError, type AxiosResponse } from 'axios'
import { store } from 'store/store'
import { setErrorMessage } from 'store/app.slice'
import { checkTokenIsExpired, refreshToken } from 'common/authentication/auth.service'

declare module 'axios' {
  export interface AxiosResponse<T = any> extends Promise<T> {}
}

const REQUEST_TIMEOUT_MS = 5000

export class ApiError extends Error {
  public statusCode: number
  public error: string
  public data: any
  public code: string | undefined

  constructor(message: string, statusCode: number, code?: string, data?: any) {
    super(message)
    this.name = this.constructor.name
    this.statusCode = statusCode
    this.error = message
    this.data = data
    this.code = code
  }
}

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_REST_API_URL,
  headers: {
    'Content-type': 'application/json',
  },
  timeout: REQUEST_TIMEOUT_MS,
})

export const setAccessToken = (token?: string): void => {
  axiosInstance.defaults.headers.common.Authorization = token ? `Bearer ${token}` : false
}
export const setSpoofToken = (impersonationToken?: string): void => {
  axiosInstance.defaults.headers.common.ImpersonationToken = impersonationToken
}

_addResponseInterceptors()

function _addResponseInterceptors(): void {
  const handleErrorResponse = async (res: AxiosError<any, any>): Promise<ApiError> => {
    console.error('::::::::: API RESPONSE ERROR ::::::::::\n', JSON.parse(JSON.stringify(res)))
    const { config } = res
    const status = res.response?.status ?? 500

    const message = res?.response?.data?.message
    const code = res?.response?.data?.code

    if (config && res.code === 'ERR_NETWORK') {
      // HACK: request might be due to expired token, might just be a network error
      // issue: https://github.com/axios/axios/issues/3877
      if (!!config.headers?.Authorization && config.maxRedirects !== 42) {
        const token = (config.headers.Authorization as string).split(' ')[1]
        if (token && checkTokenIsExpired(token) && config.maxRedirects !== 42) {
          console.error('token expired, refresh token')
          return await refreshToken()
            .then(async (token) => {
              setAccessToken(token)
              if (config?.headers) {
                config.headers.Authorization = `Bearer ${token}`
              }
              // HACK: set maxRedirect to some magic number to stop an infinite loop of requests
              return await axiosInstance.request({ ...config, maxRedirects: 42 })
            })
            .catch((err) => {
              console.error('refresh token error: ', err)
              // don't try again to avoid an infinite loop
              throw new ApiError('User not authenticated.', status, code)
            })
        }
      }
      throw new ApiError('User not authenticated.', status, code)
    }

    if (status === 403 && res?.response?.data?.code === 'account-setup-incomplete') {
      // todo check this after getVendorProfile endpoint is live
      window.location.pathname = '/kompanije/confirm'
      throw new ApiError('', status, code)
    }

    if (status >= 500) {
      store.dispatch(setErrorMessage('Unexpected server error'))
    }

    if (status === 400) {
      const errors = res.response?.data as Array<{ errors: string[]; path: string; type: string }>

      const flattenedErrors = errors.reduce((acc, curr) => {
        const errors = curr.errors.join(', ')
        return acc + errors + '\n'
      }, '')

      throw new ApiError(flattenedErrors, status, code)
    }

    throw new ApiError(message, status, code)
  }

  async function handleResponse(res: AxiosResponse): Promise<any> {
    const { data, status } = res
    if (is2xx(status)) {
      return data
    } else {
      throw new ApiError(data.message, status)
    }
  }

  function is2xx(code: number): boolean {
    return code >= 200 && code < 300
  }

  axiosInstance.interceptors.response.use(handleResponse, handleErrorResponse)
}

export default axiosInstance
